// ─── Agents/index.js ─────────────────────────────────────────────────────────
// Página /agents — CRUD + Monitoramento de Agentes IA (Fase 5)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Close,
  Delete,
  Edit,
  Search as SearchIcon,
  TrendingUp,
} from "@material-ui/icons";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { ContentCopy } from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowBuilderAddAgentModal from "../../components/FlowBuilderAddAgentModal";

// ─── Estilos ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    height: "100vh",
    flexDirection: "column",
    gap: 0,
  },
  mainPaper: {
    flex: 1,
    padding: "16px 20px",
    overflowY: "auto",
  },

  // Mini-dashboard cards
  statCard: {
    background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
    border: "1px solid #e9d5ff",
    borderRadius: 14,
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  statIcon: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statValue: {
    fontWeight: 800,
    fontSize: "1.4rem",
    color: "#4c1d95",
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },

  // Barra de busca
  searchBar: {
    marginBottom: 20,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#faf5ff",
      "& fieldset": { borderColor: "#e9d5ff" },
      "&:hover fieldset": { borderColor: "#c4b5fd" },
    },
  },

  // Cards de agente
  card: {
    background: "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%)",
    border: "1px solid #e9d5ff",
    borderRadius: 16,
    padding: "16px",
    position: "relative",
    cursor: "pointer",
    transition: "box-shadow 0.2s, transform 0.15s",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(124,58,237,0.15)",
      transform: "translateY(-2px)",
      "& $cardActions": { opacity: 1 },
      "& $cardPromptPreview": { maxHeight: 80, opacity: 1 },
    },
  },
  cardActions: {
    position: "absolute",
    top: 10,
    right: 10,
    display: "flex",
    gap: 2,
    opacity: 0,
    transition: "opacity 0.2s",
  },
  cardIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 10,
    flexShrink: 0,
  },
  cardName: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: "#1f2937",
    marginBottom: 2,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: "calc(100% - 60px)",
  },
  cardModel: {
    display: "inline-block",
    background: "#ede9fe",
    color: "#4c1d95",
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: "0.7rem",
    fontWeight: 700,
    marginBottom: 8,
  },
  cardStats: {
    display: "flex",
    gap: 10,
    marginTop: 6,
    marginBottom: 6,
    flexWrap: "wrap",
  },
  statBadge: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: "0.72rem",
    color: "#6d28d9",
    fontWeight: 600,
  },
  toolsBadge: {
    display: "inline-block",
    background: ({ toolCount }) => toolCount > 0 ? "#7c3aed" : "#9ca3af",
    color: "white",
    borderRadius: 10,
    padding: "2px 8px",
    fontSize: "0.68rem",
    fontWeight: 700,
  },
  cardPromptPreview: {
    maxHeight: 0,
    opacity: 0,
    overflow: "hidden",
    transition: "max-height 0.3s, opacity 0.3s",
    marginTop: 6,
    fontSize: "0.72rem",
    color: "#6d28d9",
    lineHeight: 1.4,
    background: "rgba(237,233,254,0.5)",
    borderRadius: 8,
    padding: "6px 8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#9ca3af",
  },
  addButton: {
    background: "#7c3aed",
    color: "white",
    fontWeight: 700,
    borderRadius: 10,
    "&:hover": { background: "#6d28d9" },
  },

  // Modal de detalhes
  detailTabs: {
    borderBottom: "1px solid #e9d5ff",
    marginBottom: 16,
  },
  detailTab: {
    minWidth: 100,
    fontWeight: 600,
    color: "#6d28d9",
    "&.Mui-selected": { color: "#4c1d95" },
  },
  execRow: {
    padding: "10px 0",
    borderBottom: "1px solid #f3e8ff",
    "&:last-child": { borderBottom: "none" },
  },
  execStatus: {
    display: "inline-block",
    borderRadius: 6,
    padding: "2px 8px",
    fontSize: "0.7rem",
    fontWeight: 700,
  },
  toolChip: {
    margin: "2px",
    fontSize: "0.68rem",
    background: "#ede9fe",
    color: "#4c1d95",
  },
}));

// ─── Mini-stat card ───────────────────────────────────────────────────────────

const StatCard = ({ icon, value, label, color = "#7c3aed" }) => {
  const classes = useStyles({});
  return (
    <div className={classes.statCard}>
      <div className={classes.statIcon} style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
        {icon}
      </div>
      <div>
        <div className={classes.statValue} style={{ color }}>{value}</div>
        <div className={classes.statLabel}>{label}</div>
      </div>
    </div>
  );
};

// ─── Status badge colors ──────────────────────────────────────────────────────

const STATUS_COLORS = {
  success:       { bg: "#d1fae5", text: "#065f46", label: "Sucesso" },
  error:         { bg: "#fee2e2", text: "#991b1b", label: "Erro" },
  timeout:       { bg: "#fef3c7", text: "#92400e", label: "Timeout" },
  forbidden_word:{ bg: "#fce7f3", text: "#9d174d", label: "Palavra Proibida" },
};

// ─── Modal de detalhes do agente ──────────────────────────────────────────────

const AgentDetailModal = ({ agent, onClose, onEdit, onDuplicate, onDelete }) => {
  const classes = useStyles({});
  const [tab, setTab] = useState(0);
  const [statsData, setStatsData] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [execPage, setExecPage] = useState(1);
  const [execTotal, setExecTotal] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingExec, setLoadingExec] = useState(false);

  const config  = agent?.config || {};
  const tools   = config.tools || {};
  const toolsOn = Object.entries(tools).filter(([, v]) => v).map(([k]) => k);

  const fetchStats = useCallback(async () => {
    if (!agent) return;
    try {
      setLoadingStats(true);
      const { data } = await api.get(`/agents/${agent.id}/stats`);
      setStatsData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingStats(false);
    }
  }, [agent]);

  const fetchExecutions = useCallback(async (page = 1) => {
    if (!agent) return;
    try {
      setLoadingExec(true);
      const { data } = await api.get(`/agents/${agent.id}/executions`, { params: { page, limit: 20 } });
      setExecutions(prev => page === 1 ? data.data : [...prev, ...data.data]);
      setExecTotal(data.total);
      setExecPage(page);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingExec(false);
    }
  }, [agent]);

  useEffect(() => {
    if (tab === 1) {
      fetchStats();
      fetchExecutions(1);
    }
  }, [tab, fetchStats, fetchExecutions]);

  if (!agent) return null;

  return (
    <Dialog open={!!agent} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle style={{ background: "linear-gradient(135deg, #faf5ff, #f5f3ff)", borderBottom: "1px solid #e9d5ff", padding: "12px 20px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#7c3aed,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SmartToyIcon style={{ color: "white", fontSize: 18 }} />
            </div>
            <div>
              <Typography style={{ fontWeight: 700, color: "#1f2937", lineHeight: 1.2 }}>{agent.name}</Typography>
              <span style={{ display: "inline-block", background: "#ede9fe", color: "#4c1d95", borderRadius: 6, padding: "1px 8px", fontSize: "0.7rem", fontWeight: 700 }}>
                {config.model || "gpt-4o"}
              </span>
            </div>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Tooltip title="Editar"><IconButton size="small" onClick={() => { onClose(); onEdit(agent); }}><Edit style={{ fontSize: 16, color: "#7c3aed" }} /></IconButton></Tooltip>
            <Tooltip title="Duplicar"><IconButton size="small" onClick={() => { onDuplicate(agent); onClose(); }}><ContentCopy style={{ fontSize: 16, color: "#7c3aed" }} /></IconButton></Tooltip>
            <Tooltip title="Deletar"><IconButton size="small" onClick={() => { onClose(); onDelete(agent); }}><Delete style={{ fontSize: 16, color: "#ef4444" }} /></IconButton></Tooltip>
            <IconButton size="small" onClick={onClose}><Close style={{ fontSize: 16 }} /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        className={classes.detailTabs}
        style={{ paddingLeft: 20 }}
        TabIndicatorProps={{ style: { background: "#7c3aed" } }}
      >
        <Tab label="Configuração" className={classes.detailTab} />
        <Tab label="Histórico" className={classes.detailTab} icon={<TrendingUp style={{ fontSize: 14 }} />} iconPosition="start" />
      </Tabs>

      <DialogContent style={{ padding: "16px 20px", minHeight: 400 }}>
        {/* ── Aba Configuração ─────────────────────────────────────────────── */}
        {tab === 0 && (
          <Box>
            {/* Stats de uso */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={4}>
                <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                  <div style={{ fontWeight: 800, fontSize: "1.3rem", color: "#7c3aed" }}>{agent.usageCount || 0}</div>
                  <div style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Execuções</div>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                  <div style={{ fontWeight: 800, fontSize: "1.3rem", color: "#7c3aed" }}>${(agent.totalCostUsd || 0).toFixed(4)}</div>
                  <div style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Custo Total</div>
                </div>
              </Grid>
              <Grid item xs={4}>
                <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                  <div style={{ fontWeight: 800, fontSize: "1.3rem", color: "#7c3aed" }}>{toolsOn.length}/12</div>
                  <div style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Tools Ativas</div>
                </div>
              </Grid>
            </Grid>

            {/* Prompt */}
            {config.systemPrompt && (
              <div style={{ marginBottom: 14 }}>
                <Typography variant="caption" style={{ fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>Prompt</Typography>
                <div style={{ background: "#f5f3ff", borderRadius: 8, padding: "10px 12px", marginTop: 6, fontSize: "0.82rem", color: "#374151", maxHeight: 120, overflowY: "auto", lineHeight: 1.5 }}>
                  {config.systemPrompt}
                </div>
              </div>
            )}

            {/* Ferramentas ativas */}
            {toolsOn.length > 0 && (
              <div>
                <Typography variant="caption" style={{ fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>Ferramentas Ativas</Typography>
                <Box style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {toolsOn.map(t => (
                    <Chip key={t} size="small" label={t.replace(/_/g, " ")} className={classes.toolChip} />
                  ))}
                </Box>
              </div>
            )}
          </Box>
        )}

        {/* ── Aba Histórico ─────────────────────────────────────────────────── */}
        {tab === 1 && (
          <Box>
            {loadingStats ? (
              <Box display="flex" justifyContent="center" mt={4}><CircularProgress style={{ color: "#7c3aed" }} /></Box>
            ) : statsData ? (
              <>
                {/* Cards de stats */}
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                  <Grid item xs={3}>
                    <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "#7c3aed" }}>{statsData.totalExecutions}</div>
                      <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Execuções</div>
                    </div>
                  </Grid>
                  <Grid item xs={3}>
                    <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "#7c3aed" }}>${(statsData.totalCost || 0).toFixed(4)}</div>
                      <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Custo USD</div>
                    </div>
                  </Grid>
                  <Grid item xs={3}>
                    <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "#7c3aed" }}>{Math.round(statsData.avgDuration || 0)}ms</div>
                      <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Duração Média</div>
                    </div>
                  </Grid>
                  <Grid item xs={3}>
                    <div style={{ background: "#f5f3ff", borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "#7c3aed" }}>{statsData.byStatus?.success || 0}</div>
                      <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>Sucessos</div>
                    </div>
                  </Grid>
                </Grid>

                {/* Gráfico de uso diário */}
                {statsData.dailyChart?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>Execuções nos últimos 30 dias</Typography>
                    <div style={{ marginTop: 10, height: 120 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statsData.dailyChart} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
                          <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d?.substring(5) || ""} />
                          <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                          <RechartsTooltip
                            labelFormatter={l => `Data: ${l}`}
                            formatter={v => [v, "Execuções"]}
                            contentStyle={{ fontSize: 11, borderRadius: 8 }}
                          />
                          <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {/* Top ferramentas usadas */}
                {Object.keys(statsData.toolsUsage || {}).length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography variant="caption" style={{ fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>Ferramentas mais usadas</Typography>
                    <div style={{ marginTop: 8 }}>
                      {Object.entries(statsData.toolsUsage)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 5)
                        .map(([tool, count]) => (
                          <div key={tool} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <div style={{ fontSize: "0.75rem", color: "#374151", minWidth: 160 }}>{tool.replace(/_/g, " ")}</div>
                            <div style={{ flex: 1, height: 6, background: "#ede9fe", borderRadius: 4 }}>
                              <div style={{ width: `${Math.min(100, (count / Math.max(...Object.values(statsData.toolsUsage))) * 100)}%`, height: "100%", background: "#7c3aed", borderRadius: 4 }} />
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "#7c3aed", fontWeight: 700, minWidth: 24, textAlign: "right" }}>{count}</div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}

            {/* Lista de execuções */}
            <Typography variant="caption" style={{ fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", letterSpacing: "0.05em" }}>Últimas execuções</Typography>
            {loadingExec && executions.length === 0 ? (
              <Box display="flex" justifyContent="center" mt={2}><CircularProgress size={24} style={{ color: "#7c3aed" }} /></Box>
            ) : executions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "20px", color: "#9ca3af", fontSize: "0.82rem" }}>Nenhuma execução registrada</div>
            ) : (
              <>
                {executions.map(exec => {
                  const sc = STATUS_COLORS[exec.status] || STATUS_COLORS.success;
                  const tools = Array.isArray(exec.toolsCalled) ? exec.toolsCalled : [];
                  return (
                    <div key={exec.id} className={classes.execRow}>
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 4 }}>
                            <span className={classes.execStatus} style={{ background: sc.bg, color: sc.text }}>{sc.label}</span>
                            <span style={{ fontSize: "0.7rem", color: "#9ca3af" }}>
                              {new Date(exec.createdAt).toLocaleString("pt-BR")} · {exec.durationMs}ms · ${(exec.costUsd || 0).toFixed(5)}
                            </span>
                            {exec.ticketId && (
                              <span style={{ fontSize: "0.7rem", color: "#7c3aed", fontWeight: 600 }}>Ticket #{exec.ticketId}</span>
                            )}
                          </Box>
                          {exec.input && (
                            <div style={{ fontSize: "0.78rem", color: "#374151", marginBottom: 3 }}>
                              <strong>Entrada:</strong> {exec.input.substring(0, 100)}{exec.input.length > 100 ? "…" : ""}
                            </div>
                          )}
                          {exec.output && (
                            <div style={{ fontSize: "0.78rem", color: "#6b7280", marginBottom: 4 }}>
                              <strong>Saída:</strong> {exec.output.substring(0, 120)}{exec.output.length > 120 ? "…" : ""}
                            </div>
                          )}
                          {tools.length > 0 && (
                            <Box style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                              {tools.map((t, i) => (
                                <Chip
                                  key={i}
                                  size="small"
                                  label={`${t.success ? "✓" : "✗"} ${t.tool?.replace(/_/g, " ")}`}
                                  className={classes.toolChip}
                                  style={{ background: t.success ? "#d1fae5" : "#fee2e2", color: t.success ? "#065f46" : "#991b1b" }}
                                />
                              ))}
                            </Box>
                          )}
                          {exec.errorMessage && (
                            <div style={{ fontSize: "0.72rem", color: "#991b1b", marginTop: 4 }}>
                              ⚠ {exec.errorMessage}
                            </div>
                          )}
                        </Box>
                      </Box>
                    </div>
                  );
                })}
                {executions.length < execTotal && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ borderColor: "#c4b5fd", color: "#7c3aed" }}
                      onClick={() => fetchExecutions(execPage + 1)}
                      disabled={loadingExec}
                    >
                      {loadingExec ? <CircularProgress size={14} /> : "Carregar mais"}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Componente de card ───────────────────────────────────────────────────────

const AgentCard = ({ agent, onView, onEdit, onDuplicate, onDelete }) => {
  const config   = agent.config || {};
  const tools    = config.tools || {};
  const toolCount = Object.values(tools).filter(Boolean).length;
  const classes  = useStyles({ toolCount });

  const promptPreview = config.systemPrompt
    ? config.systemPrompt.substring(0, 120) + (config.systemPrompt.length > 120 ? "…" : "")
    : "Sem prompt configurado";

  return (
    <div className={classes.card} onClick={() => onView(agent)}>
      {/* Ações no hover */}
      <div className={classes.cardActions}>
        <Tooltip title="Detalhes/Editar">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onEdit(agent); }}>
            <Edit style={{ fontSize: 15, color: "#7c3aed" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicar">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onDuplicate(agent); }}>
            <ContentCopy style={{ fontSize: 15, color: "#7c3aed" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Deletar">
          <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(agent); }}>
            <Delete style={{ fontSize: 15, color: "#ef4444" }} />
          </IconButton>
        </Tooltip>
      </div>

      {/* Ícone + nome */}
      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
        <div className={classes.cardIcon}>
          <SmartToyIcon style={{ color: "white", fontSize: 20 }} />
        </div>
        <div style={{ flex: 1, overflow: "hidden" }}>
          <Typography className={classes.cardName} title={agent.name}>
            {agent.name}
          </Typography>
          <span className={classes.cardModel}>{config.model || "gpt-4o"}</span>
        </div>
      </Box>

      {/* Stats */}
      <div className={classes.cardStats}>
        <span className={classes.statBadge}>
          🔧 <span className={classes.toolsBadge}>{toolCount}/12 tools</span>
        </span>
        <span className={classes.statBadge}>
          📊 {agent.usageCount || 0} usos
        </span>
        <span className={classes.statBadge}>
          💰 ${(agent.totalCostUsd || 0).toFixed(4)}
        </span>
      </div>

      {/* Preview do prompt (visível no hover) */}
      <div className={classes.cardPromptPreview}>
        {promptPreview}
      </div>
    </div>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────

const AgentsPage = () => {
  const classes = useStyles({});

  const [agents, setAgents]                 = useState([]);
  const [loading, setLoading]               = useState(true);
  const [search, setSearch]                 = useState("");
  const [modalOpen, setModalOpen]           = useState(null); // "create" | "edit" | null
  const [editingAgent, setEditingAgent]     = useState(null);
  const [confirmDelete, setConfirmDelete]   = useState(null);
  const [viewingAgent, setViewingAgent]     = useState(null); // modal detalhe
  const [companyStats, setCompanyStats]     = useState(null);

  // ── Buscar dados ─────────────────────────────────────────────────────────

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/agents");
      setAgents(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompanyStats = useCallback(async () => {
    try {
      const { data } = await api.get("/agents/company-stats");
      setCompanyStats(data);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    fetchAgents();
    fetchCompanyStats();
  }, [fetchAgents, fetchCompanyStats]);

  // ── Filtro de busca ───────────────────────────────────────────────────────

  const filtered = agents.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase())
  );

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCreate = async ({ agentSettings }) => {
    try {
      const { data } = await api.post("/agents", {
        name:   agentSettings.name,
        config: agentSettings,
      });
      setAgents(prev => [data, ...prev]);
      toast.success("Agente criado com sucesso!");
      fetchCompanyStats();
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpdate = async (nodeData) => {
    const { id, data: { agentSettings } } = nodeData;
    try {
      const { data } = await api.put(`/agents/${id}`, {
        name:   agentSettings.name,
        config: agentSettings,
      });
      setAgents(prev => prev.map(a => a.id === data.id ? data : a));
      toast.success("Agente atualizado!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicate = async (agent) => {
    try {
      const { data } = await api.post(`/agents/${agent.id}/duplicate`);
      setAgents(prev => [data, ...prev]);
      toast.success(`Agente "${data.name}" criado!`);
      fetchCompanyStats();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/agents/${confirmDelete.id}`);
      setAgents(prev => prev.filter(a => a.id !== confirmDelete.id));
      toast.success("Agente removido.");
      fetchCompanyStats();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmDelete(null);
    }
  };

  const openEdit = (agent) => {
    setEditingAgent(agent);
    setModalOpen("edit");
  };

  const openCreate = () => {
    setEditingAgent(null);
    setModalOpen("create");
  };

  const closeModal = () => {
    setModalOpen(null);
    setEditingAgent(null);
  };

  // Formato do data prop que o modal espera para edição
  const modalData = editingAgent
    ? { ...editingAgent, data: { agentSettings: editingAgent.config } }
    : null;

  return (
    <MainContainer className={classes.root}>
      <MainHeader>
        <Title>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <SmartToyIcon style={{ color: "#7c3aed", fontSize: 24 }} />
            Agentes IA
          </Box>
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            className={classes.addButton}
            startIcon={<AddIcon />}
            onClick={openCreate}
          >
            Novo Agente
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className={classes.mainPaper}>
        {/* Mini-dashboard */}
        {companyStats && (
          <Grid container spacing={2} style={{ marginBottom: 8 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<SmartToyIcon style={{ color: "white", fontSize: 20 }} />}
                value={companyStats.totalAgents}
                label="Agentes Ativos"
                color="#7c3aed"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<TrendingUp style={{ color: "white", fontSize: 20 }} />}
                value={companyStats.totalExecutions}
                label="Execuções Totais"
                color="#6d28d9"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<span style={{ color: "white", fontSize: 16, fontWeight: 800 }}>$</span>}
                value={`$${(companyStats.totalCost || 0).toFixed(3)}`}
                label="Custo Acumulado"
                color="#5b21b6"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<span style={{ color: "white", fontSize: 13, fontWeight: 800 }}>🔧</span>}
                value={companyStats.topTool?.name?.replace(/_/g, " ").substring(0, 14) || "—"}
                label={`Tool Mais Usada${companyStats.topTool ? ` (${companyStats.topTool.count}x)` : ""}`}
                color="#4c1d95"
              />
            </Grid>
          </Grid>
        )}

        {/* Busca */}
        <TextField
          className={classes.searchBar}
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Buscar agentes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "#c4b5fd" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Conteúdo */}
        {loading ? (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress style={{ color: "#7c3aed" }} />
          </Box>
        ) : filtered.length === 0 ? (
          <div className={classes.emptyState}>
            <SmartToyIcon style={{ fontSize: 56, color: "#c4b5fd", marginBottom: 12 }} />
            <Typography variant="h6" style={{ color: "#6d28d9", fontWeight: 600 }}>
              {search ? "Nenhum agente encontrado" : "Nenhum agente criado ainda"}
            </Typography>
            <Typography variant="body2" style={{ color: "#9ca3af", marginTop: 4 }}>
              {search
                ? `Tente buscar por outro nome`
                : "Crie seu primeiro agente para usar nos fluxos"
              }
            </Typography>
            {!search && (
              <Button
                variant="contained"
                className={classes.addButton}
                startIcon={<AddIcon />}
                onClick={openCreate}
                style={{ marginTop: 20 }}
              >
                Criar primeiro agente
              </Button>
            )}
          </div>
        ) : (
          <Grid container spacing={2}>
            {filtered.map(agent => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={agent.id}>
                <AgentCard
                  agent={agent}
                  onView={setViewingAgent}
                  onEdit={openEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={setConfirmDelete}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </div>

      {/* Modal de detalhes do agente */}
      <AgentDetailModal
        agent={viewingAgent}
        onClose={() => setViewingAgent(null)}
        onEdit={openEdit}
        onDuplicate={handleDuplicate}
        onDelete={setConfirmDelete}
      />

      {/* Modal criar/editar */}
      <FlowBuilderAddAgentModal
        open={modalOpen}
        onSave={handleCreate}
        data={modalData}
        onUpdate={handleUpdate}
        close={closeModal}
      />

      {/* Modal de confirmação de delete */}
      <ConfirmationModal
        title={`Deletar agente "${confirmDelete?.name}"?`}
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
      >
        Todos os fluxos que referenciam este agente por ID deixarão de funcionar.
        Agentes inline (config no nó) não são afetados.
      </ConfirmationModal>
    </MainContainer>
  );
};

export default AgentsPage;
